import { UserPermissionRole } from "@prisma/client";
import classNames from "classnames";
import { signOut } from "next-auth/react";
import { useRouter } from "next/router";
import React, { useState } from "react";

import useGroupOrSingleView from "@calcom/lib/hooks/useGroupOrSingleView";
import { trpc } from "@calcom/trpc/react";
import type { SVGComponent } from "@calcom/types/SVGComponent";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
} from "@calcom/ui";
import { DollarIcon, MailIcon, MobilePhoneIcon, UserIcon } from "@calcom/web/components/Icons";

import { useBlueStrip } from "@lib/blueStrip";

import { CreateOfficeDialog } from "@components/dialog/OfficeDialog";
import NotificationBell from "@components/navbar/NotificationBell";
import { useOfficeContext } from "@components/navbar/OfficeProvider";
import OfficeSelect from "@components/navbar/OfficeSelect";
import Search from "@components/navbar/Search";
import CreatePaymentDialog from "@components/patients/CreatePaymentDialog";

const appId = process.env.FINIXAPPID || "";
const env = process.env.FINIXENV || "";

const navActionClassName =
  "h-9 rounded-lg border border-r-gray-200 bg-white text-gray-600 hover:bg-white hover:text-gray-600";

function NavActionButton({
  label,
  Icon,
  onClick,
}: {
  label: string;
  Icon: SVGComponent;
  onClick: () => void;
}) {
  return (
    <>
      <Tooltip content={label}>
        <Button
          aria-label={label}
          className={classNames(navActionClassName, "flex w-9 items-center justify-center !px-0 xl:hidden")}
          onClick={onClick}>
          <Icon className="h-4 w-4" />
        </Button>
      </Tooltip>
      <Button className={classNames(navActionClassName, "hidden xl:flex")} onClick={onClick}>
        {label}
      </Button>
    </>
  );
}

const Profile = ({ name, email }: { name?: string | null; email?: string | null }) => {
  const router = useRouter();

  return (
    <Dropdown modal={false}>
      <DropdownMenuTrigger asChild data-testid="event-type-options-">
        <div className="flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-gray-200 px-2 py-1">
          {name && <span className="max-w-[120px] truncate text-sm font-medium text-gray-700">{name}</span>}
          {name && <span className="text-gray-300">|</span>}
          <UserIcon />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        style={{
          marginTop: "10px",
          width: "200px",
        }}>
        {(name || email) && (
          <div className="border-b border-gray-200 px-3 py-2">
            {name && <p className="truncate text-sm font-medium text-gray-900">{name}</p>}
            {email && <p className="truncate text-xs text-gray-500">{email}</p>}
          </div>
        )}
        <DropdownMenuItem className="rounded-lg">
          <DropdownItem
            onClick={() => {
              router.push("/user-settings");
            }}>
            User Settings
          </DropdownItem>
        </DropdownMenuItem>
        <DropdownMenuItem className="rounded-lg" onClick={() => signOut({ callbackUrl: "/auth/logout" })}>
          <DropdownItem type="button" data-testid="event-type-edit-" className="rounded-lg">
            Log Out
          </DropdownItem>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </Dropdown>
  );
};

function TopNavbar() {
  const { data: user, isLoading } = trpc.viewer.me.useQuery();

  const [createPaymentDlg, setCreatePaymentDlg] = useState(false);
  const [officeDlgOpen, setOfficeDlgOpen] = useState(false);

  const { currentOffice } = useOfficeContext();

  const isGroupView = useGroupOrSingleView();

  const { isHidden } = useBlueStrip();

  const showNotificationBell =
    user?.role === UserPermissionRole.ADMIN || user?.role === UserPermissionRole.GROUP_ADMIN;

  return (
    <div
      className={classNames(
        "fixed z-10 h-[52px] w-full border-b border-gray-200 bg-white",
        isHidden ? "top-0" : "top-8"
      )}>
      <div className="absolute h-full w-52 bg-white md:bg-[#F9FAFA]" />
      <div className="absolute left-1/2 h-full w-1/2 bg-white" />
      <div className="relative mx-auto h-[52px] w-full flex-col items-center md:flex md:flex-row md:gap-0 md:p-0">
        <div className="flex border-0 border-gray-200 md:mb-0 md:flex-row md:border-r">
          <div className="flex h-[52px] w-[250px] items-center gap-3 px-3 py-2 md:w-[207px]">
            {!isLoading && (
              <>
                {isGroupView || !user?.isGroupView ? (
                  <img src={user?.logo || ""} alt="billing logo" className="h-7" />
                ) : (
                  <OfficeSelect />
                )}
              </>
            )}
          </div>
          <div className="flex-grow" />
          <div className="flex items-center gap-4 py-2 pr-4 md:hidden md:pr-0">
            {showNotificationBell && <NotificationBell />}
            <Profile name={user?.name} email={user?.email} />
          </div>
        </div>
        <div className="mx-auto mt-2 flex w-full max-w-7xl bg-white px-4 md:mt-0 md:px-6">
          {!isGroupView && currentOffice && (
            <div className="mb-4 hidden w-full items-center md:mb-0 md:flex md:w-auto">
              <Search officeId={currentOffice} />
            </div>
          )}

          <div className="hidden flex-grow md:block" />
          {isGroupView && user?.role === UserPermissionRole.ADMIN && (
            <div className="mb-2 mr-4 hidden justify-around gap-4 md:mb-0 md:flex md:justify-start">
              <Button
                className="h-9 rounded-lg border border-r-gray-200 bg-white text-gray-600 hover:bg-white hover:text-gray-600"
                onClick={() => {
                  setOfficeDlgOpen(true);
                }}>
                Add Location
              </Button>
            </div>
          )}
          {!isGroupView && (
            <div className="mx-4 hidden items-center gap-4 md:flex">
              <NavActionButton
                label="Email Support"
                Icon={MailIcon}
                onClick={() => {
                  window.location.href = "mailto:support@quantasuite.com";
                }}
              />
              <NavActionButton
                label="Call Support : (833) 511-2356"
                Icon={MobilePhoneIcon}
                onClick={() => {
                  window.location.href = "tel:+18335112356";
                }}
              />
              {user?.createPayment && (
                <NavActionButton
                  label="Create Payment"
                  Icon={DollarIcon}
                  onClick={() => {
                    setCreatePaymentDlg(true);
                  }}
                />
              )}
            </div>
          )}
          <div className="hidden items-center gap-3 md:flex">
            {showNotificationBell && <NotificationBell />}
            <Profile name={user?.name} email={user?.email} />
          </div>
        </div>
      </div>
      {createPaymentDlg && currentOffice && (
        <CreatePaymentDialog
          open={createPaymentDlg}
          handler={setCreatePaymentDlg}
          officeId={currentOffice}
          appId={appId}
          env={env}
        />
      )}
      {officeDlgOpen && <CreateOfficeDialog open={officeDlgOpen} handler={setOfficeDlgOpen} isNew={true} />}
    </div>
  );
}

export default TopNavbar;
